import { useEffect, useState } from "react";

import { SetURLSearchParams } from "react-router-dom";

export const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_INITIAL_PAGE = 0;

export interface Pagination {
  currentPage: number;
  goToNextPage: () => void;
  goToPage: (page: number) => void;
  goToPreviousPage: () => void;
  hasNext: boolean;
  hasPrevious: boolean;
  numItems: number;
  numPages: number;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
}

interface PaginationQueryParamOptions {
  queryParamName: string;
  setQueryParams: SetURLSearchParams;
  queryParams: URLSearchParams;
}

interface UsePaginationOptions {
  initialPage?: number;
  loading?: boolean;
  pageSize?: number;
  resetScrollOnPageChange?: boolean;
  queryParamOptions?: PaginationQueryParamOptions;
}

const usePagination = <T>(items: T[], options?: UsePaginationOptions) => {
  const initialPageSize = options?.pageSize ?? DEFAULT_PAGE_SIZE;
  const initialNumPages = Math.max(
    1,
    Math.ceil(items.length / initialPageSize)
  );
  const initialPage = Math.max(options?.initialPage ?? DEFAULT_INITIAL_PAGE, 0);

  const [pageSize, setPageSize] =
    useState<Pagination["pageSize"]>(initialPageSize);
  const [currentPage, setCurrentPage] =
    useState<Pagination["currentPage"]>(initialPage);
  const [numPages, setNumPages] =
    useState<Pagination["numPages"]>(initialNumPages);

  const numItems = items.length;

  const changePage = (opts: { delta: number } | { page: number }) => {
    if (options?.resetScrollOnPageChange) {
      document.body.scrollTop = document.documentElement.scrollTop = 0;
    }

    if ("delta" in opts) {
      setCurrentPage((currentPage) => currentPage + opts.delta);
    } else {
      setCurrentPage(opts.page);
    }
  };

  useEffect(() => {
    if (options?.loading) return;

    const numPages = Math.ceil(numItems / pageSize);
    if (options?.queryParamOptions) {
      const { queryParams, setQueryParams, queryParamName } =
        options.queryParamOptions;
      const page = Number(queryParams.get(queryParamName));
      // clear page query param if invalid value
      if (!page || page > numPages - 1 || page < 0)
        setQueryParams((prev) => {
          const newParams = prev;
          newParams.delete(queryParamName);
          return newParams;
        });
    }
    setNumPages(numPages);
    const highestPage = Math.max(numPages - 1, 0);
    if (currentPage > highestPage) {
      setCurrentPage(highestPage);
    }
  }, [numItems, pageSize, options?.loading]);

  const goToPage: Pagination["goToPage"] = (page) =>
    changePage({ page: Math.max(0, page) });

  const goToNextPage: Pagination["goToNextPage"] = () => {
    if (options?.queryParamOptions) {
      const pageMovement = currentPage + 1;
      const params = new URLSearchParams([
        [options.queryParamOptions.queryParamName, pageMovement.toString()],
      ]);
      options.queryParamOptions.setQueryParams(params);
    }
    changePage({ delta: 1 });
  };
  const goToPreviousPage: Pagination["goToPreviousPage"] = () => {
    if (options?.queryParamOptions) {
      const pageMovement = currentPage - 1;
      const params = new URLSearchParams([
        [options.queryParamOptions.queryParamName, pageMovement.toString()],
      ]);
      options.queryParamOptions.setQueryParams(params);
    }
    changePage({ delta: -1 });
  };

  const handlePageSizeChange: Pagination["setPageSize"] = (pageSize) => {
    setPageSize(pageSize);
    setCurrentPage(DEFAULT_INITIAL_PAGE);
  };

  return {
    currentPage,
    goToNextPage,
    goToPage,
    goToPreviousPage,
    hasNext: currentPage < numPages - 1,
    hasPrevious: currentPage !== 0,
    numItems,
    numPages,
    pageSize,
    setPageSize: handlePageSizeChange,
  };
};

export default usePagination;
